import React, { useMemo, memo, useEffect, useRef, forwardRef } from 'react'
import styled, { css, keyframes } from 'styled-components';
import { groupBy, sum, pluck, sort } from 'ramda';
import { differenceInDays, parse, addDays, format, startOfWeek } from 'date-fns';

import { colors, neumorphism } from '../constant';
import { secondToHM } from '../methods';
import { respondTo } from '../responsive';

const BarChart = ({ startAt, endAt, data }) => {

    const postData = useMemo(() => {
        const parseStartAt = parse(startAt.replace(' 00:00:00', ''), 'yyyy-MM-dd', new Date());
        const startAtStartOfWeek = startOfWeek(parseStartAt, { weekStartsOn: 1 });
        
        const _postData = data?.map(item => {
            const parseItemStartAt = parse(item.start_at, 'yyyy-MM-dd', new Date());
            return {
                ...item,
                week: ~~((differenceInDays(parseItemStartAt, startAtStartOfWeek) + 1) / 7) + 1
            }
        });

        let dataGroupByWeek = groupBy(item => item.week, _postData);
        let maxDuration = 0;

        Object.keys(dataGroupByWeek).forEach(key => {
            const sumDuration = sum(pluck('weighted_duration', dataGroupByWeek[key]));
            dataGroupByWeek[key] = {
                weekStart: format(addDays(startAtStartOfWeek, (key - 1) * 7), 'yyyy/MM/dd'),
                weekEnd: format(addDays(startAtStartOfWeek, (key - 1) * 7 + 6), 'yyyy/MM/dd'),
                sumDuration: sumDuration,
                detail: groupBy(item => item.user_name, dataGroupByWeek[key])
            }
            if (maxDuration < sumDuration) maxDuration = sumDuration;
        });

        const diffMin = (a, b) => a - b;
        const diffMax = (a, b) => b - a;
        const weekStart = Math.max(~~(sort(diffMin, Object.keys(dataGroupByWeek))?.[0] || 0), 0);
        const weekEnd = Math.max(~~(sort(diffMax, Object.keys(dataGroupByWeek))?.[0] || 0), 0);

        return { dataGroupByWeek, maxDuration, weekStart, weekEnd };
    }, [data]);

    const { dataGroupByWeek, maxDuration, weekStart, weekEnd } = postData;
    return (
        <Root>
            { (new Array(weekEnd)).fill().map?.((_, index) => 
                <BarItem key={index} week={index + 1} item={dataGroupByWeek[index + 1] || null} max={maxDuration}/>
            )}
            <Empty></Empty>
            { (!data || data?.length === 0) && <NoData>尚未有資料可以做圖</NoData> }
        </Root>
    )
}

const Root = styled.div`
    padding: 12px 24px;
    border-radius: 16px;

    background-color: #F1F1F1;
    
    display: flex;
    flex-wrap: nowrap;
    overflow-x: auto;
    overflow-y: visible;

    ${ neumorphism.concave };

    ${ respondTo.md } {
        border-radius: 8px;
    }
`

const NoData = styled.div`
    color: ${ colors.label2 };
    font-size: 16px;
    font-weight: bold;
    flex: 1;
    height: 160px;
    display: flex;
    justify-content: center;
    align-items: center;
`

const Empty = styled.div`
    width: 1px;
    height: 1px;
    &::before {
        display: block;
        content: '.';
        color: transparent;
    }
`

const BarItem = ({week, item, max}) => {
    const rootRef = useRef(null);
    const barPopupRef = useRef(null);

    const mouseEnter = e => {
        barPopupRef.current = rootRef.current.querySelector('.popup');
        if (!barPopupRef.current) return;

        const parentScrollLeft = rootRef.current.parentNode.scrollLeft;

        const bar = rootRef.current.querySelector('.bar');
        barPopupRef.current.style.display = 'block';

        const rect = bar.getBoundingClientRect();
        // barPopupRef.current.style.left = rect.left + rect.width * 0.25 + 'px';
        barPopupRef.current.style.transform = `translate(${-parentScrollLeft}px, ${-rect?.height - 32}px)`;
    }

    const mouseLeave = e => {
        if (!barPopupRef.current) return;
        barPopupRef.current.style.display = 'none';
    }

    useEffect(() => {
        rootRef.current.addEventListener('mouseenter', mouseEnter);
        rootRef.current.addEventListener('mouseleave', mouseLeave);

        return () => {
            rootRef.current.removeEventListener('mouseenter', mouseEnter);
            rootRef.current.removeEventListener('mouseleave', mouseLeave);
        }
    }, []);

    return (
        <BarRoot ref={rootRef}>
            { item && <BarHoverPopup className="popup" item={item} week={week}/> }
            { item && <Bar className="bar" height={item.sumDuration / max}/> }
            <span>{ week }</span>
        </BarRoot>
    )
}

const BarRoot = styled.div`
    position: relative;
    height: 240px;
    width: 32px;
    margin-right: 24px;

    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    align-items: center;

    flex-shrink: 0;

    >span {
        margin-top: 4px;
        color: ${ colors.label2 };

        &::before{
            content: 'w';
        }
    }

    .popup {
        pointer-events: none;
        display: none;
    }

    ${ respondTo.md } {
        height: 120px;
        width: 16px;
        margin-right: 8px;
        >span {
            font-size: 11px;
            &::before{
                content: '';
            }
        }
    }

`

const BarEnter = height => keyframes`
    0% {
        height: 0%;
    }
    100% {
        height: ${height}%;
    }
`

const Bar = styled.div`
    width: 100%;
    height: ${({height}) => (height * 0.9 + 0.1) * 100}%;
    border-radius: 50em;
    background-color: ${ colors.primaryGradien };
    
    animation: ${BarEnter(({height}) => (height * 0.9 + 0.1) * 100)} 1s ease forwards;

    min-height: 32px;

    ${ respondTo.md } {
        min-height: 16px;
    }
`

const BarHoverPopup = memo(({ item, week, className }) => {
    let barSum = 0;
    return (
        <BarPopupWrapper className={className}>
            <BarHoverPopupRoot>
                <h2>Week { week }</h2>
                <div className="date">{`${item.weekStart} - ${item.weekEnd}`}</div>
                { Object.keys(item.detail).map?.((key, index) => {  
                    let detailSum = sum(pluck('weighted_duration', item.detail[key]));
                    barSum += detailSum;
                    return <DetailItem key={`di${index}`}>
                        <span>{ key }</span>
                        <span>{ secondToHM(detailSum) }</span>
                    </DetailItem>
                })}
                <DetailItem sumItem>
                    <span>小計</span>
                    <span>{ secondToHM(barSum) }</span>
                </DetailItem>
                <ArrowSvg width="23" height="20" viewBox="0 1 23 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M11.5 20L0.674682 0.5L22.3253 0.500002L11.5 20Z" fill="#FCFCFC"/>
                </ArrowSvg>

            </BarHoverPopupRoot>
        </BarPopupWrapper>
    )
})

const BarPopupEnter = keyframes`
    0% {
        opacity: 0;
    }
    100% {
        opacity: 1;
    }
`

const BarPopupWrapper = styled.div`
    position: fixed;
    z-index: 9;
    animation: ${BarPopupEnter} .4s forwards ease;

    ${ respondTo.md } {
        display: none !important;
    }
`

const BarHoverPopupRoot = styled.div`
    background-color: #FCFCFC;

    padding: 16px;
    width: auto;
    min-width: 240px;
    border-radius: 16px;
    color: ${ colors.text };

    box-shadow: 4px 4px 8px rgba(0, 0, 0, 0.09);

    >h2 {
        font-size: 18px;
        font-weight: bold;
        margin: 0;
    }

    >.date {
        font-size: 12px;
        color: ${ colors.label2 };
        letter-spacing: 0.05em;
        margin-bottom: 24px;
    }

    ${ respondTo.lg } {
        transform: translate(-85%, -100px);
    }
`

const DetailItem = styled.div`
    font-size: 12px;
    display: flex;
    justify-content: space-between;
    margin-bottom: 4px;

    ${({sumItem}) => sumItem && css`
        margin-bottom: 0;
        margin-top: 8px;
        padding-top: 8px;
        border-top: 1px solid ${ colors.stroke };
        font-weight: bold;
    `}
`

const ArrowSvg = styled.svg`
    position: absolute;
    bottom: 0%;
    left: 50%;
    transform: translate(-50%, 100%);
`

export default BarChart
